import { OrgRole, type OrganizationMembership } from 'pure-js/types/OrganizationMembership'
import {
  isNonOperationalOrganizationStatus,
  isPendingOnboardingStatus,
  type OrganizationStatusValue
} from 'pure-js/libs/organizationStatus'
import type { User } from 'pure-js/types/User'
import { GamerRoutes, FacilitatorRoutes } from '../../config/routes/routeEnums'
import { getActiveOrgMembership, getActiveOrganizationId } from './orgStateHelpers'

export const ORG_ONBOARDING_PATH_PREFIX = '/org/'

export function buildOrgOnboardingPath(organizationId: string): string {
  return `${ORG_ONBOARDING_PATH_PREFIX}${organizationId}/onboarding`
}

export function isOrgOnboardingPath(pathname: string): boolean {
  return /^\/org\/[^/]+\/onboarding\/?$/.test(pathname)
}

export function isInviteAcceptPath(pathname: string): boolean {
  return pathname.startsWith('/invite/')
}

export function isOnboardingOrInvitePath(pathname: string): boolean {
  return isOrgOnboardingPath(pathname) || isInviteAcceptPath(pathname)
}

export { isNonOperationalOrganizationStatus, isPendingOnboardingStatus }

/**
 * Active-tenant org admin membership (not platform super-admin without a row).
 */
export function getActiveOrgAdminMembership(user: User | null | undefined): OrganizationMembership | undefined {
  const membership = getActiveOrgMembership(user)
  if (membership?.role === OrgRole.ADMIN) return membership
  return undefined
}

export function shouldRedirectOrgAdminToOnboarding(
  user: User | null | undefined,
  organizationStatus: OrganizationStatusValue | string | undefined
): boolean {
  const adminMembership = getActiveOrgAdminMembership(user)
  if (!adminMembership) return false
  return isPendingOnboardingStatus(organizationStatus)
}

export function getOnboardingRedirectPathIfPending(
  user: User | null | undefined,
  organizationStatus: OrganizationStatusValue | string | undefined,
  organizationId?: string
): string | null {
  const orgId = organizationId ?? getActiveOrganizationId(user)
  if (!orgId || !shouldRedirectOrgAdminToOnboarding(user, organizationStatus)) {
    return null
  }
  return buildOrgOnboardingPath(orgId)
}

export function getDefaultDashboardPathForRole(role: OrgRole | 'admin'): string {
  switch (role) {
    case OrgRole.FACILITATOR:
    case OrgRole.ADMIN:
    case 'admin':
      return FacilitatorRoutes.BASE
    case OrgRole.GAMER:
    case OrgRole.LEADER:
    default:
      return GamerRoutes.BASE
  }
}

/**
 * Whether a saved `from` path should be restored after login.
 * Blocks facilitator (and other) destinations while org onboarding is still pending.
 */
export function shouldHonorIntendedDestination(
  intendedPathname: string | undefined,
  options: {
    canAccess: (path: string) => boolean
    pendingOnboardingRedirectPath: string | null
  }
): boolean {
  const { canAccess, pendingOnboardingRedirectPath } = options
  if (
    !intendedPathname ||
    intendedPathname === '/' ||
    intendedPathname === '/login' ||
    intendedPathname === '/signedin'
  ) {
    return false
  }
  if (pendingOnboardingRedirectPath && !isOnboardingOrInvitePath(intendedPathname)) {
    return false
  }
  return canAccess(intendedPathname)
}
